import usePipelinesItems from 'js/components/pipelines/ExpandedPipelines/hooks/usePipelinesItems';
import { FlowNodes } from 'js/components/pipelines/FlowNodes/FlowNodes';
import { CANVAS_HEIGHT } from 'js/components/pipelines/FlowNodes/consts';
import useFitNodeCreation from 'js/components/pipelines/FlowNodes/useFitNodeCreation';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { isSqlAutomation as isSqlAutomationSource } from 'js/lib/ResourceUtils';
import { isFullScreenCanvasURL } from 'js/lib/URLUtils';
import { isEqual } from 'lodash';
import ApiServerFlowNodes from 'new/components/flows/types/api_server/ApiServerFlowNodes';
import { ApiRequestModal } from 'new/components/flows/types/api_server/components/ApiRequestModal';
import RAGFlowNodes from 'new/components/flows/types/genai_rag/RAGFlowNodes';
import { RAGAPIUsage } from 'new/components/flows/types/genai_rag/components/RAGAPIUsage';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  flowTypeSupportsLayoutToggle,
  layoutDirectionFromSearchParams,
} from 'js/components/pipelines/FlowNodes/constants';
import type { LayoutDirection } from 'js/components/pipelines/FlowNodes/types';
import { buildToolbarItems } from 'js/components/pipelines/buildToolbarItems';
import { useAppContext } from 'new/hooks/useAppContext';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import ResourceEditProvider from '../ResourceEditProvider';

const styles = {
  mainContent: {
    marginBottom: 20,
  },
  datasetsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: 'calc(100% - 80px)',
    gap: '16px',
  },
  sendDatasetIcon: {
    height: 15,
    viewBox: '0 0 15 15',
    position: 'relative',
    top: 2,
    color: '#357BFF',
  },
};

const minimapStyle = { marginBottom: 60 };

type ResourceType = 'data_sources' | 'data_sets' | 'data_sinks';
export type SourcePipelineProps = {
  pipelineItems: any;
  flow: any;
  onRootRef?: any;
  dataSource?: any;
  onClickExpandFlow?: any;
  refreshPipeline: (id: number) => void;
  isPreview: boolean;
  isFlowRunPreview?: boolean;
  deselectAllPipelineItems?: () => void;
  getItemCard?: (item: any) => any;
  onItemClick?: (resourceType: ResourceType, resource: any) => void;
  selectedItem?: any;
  select?: (item: any) => void;
  branches?: any;
  longestDataSetChainLength?: number;
  connectionTypes?: any;
  disableHashing?: boolean;
  fullScreen?: boolean;
  projectId?: number;
  /** Hide the metrics interval toggle on the canvas (quick/structural previews). */
  hideMetricsSelector?: boolean;
  entities?: { vendors?: Record<string, { name: string }> };
  /** BYO create-flow mode: inject a virtual SOURCE shell when no real flow exists yet. */
  byoCreate?: { onSourceClick: () => void };
};

const SourcePipeline = (props: SourcePipelineProps) => {
  const {
    pipelineItems: items,
    flow,
    entities,
    onRootRef,
    onClickExpandFlow,
    refreshPipeline,
    isPreview,
    isFlowRunPreview,
    disableHashing = false,
    fullScreen = false,
  } = props;
  const { pathname, search } = useLocation();
  const history = useHistory();
  const { isEmbeddedMode } = useAppContext();
  const isFullScreenCanvas = isFullScreenCanvasURL(pathname);
  const flowNodesRef = useRef<any>();
  useFitNodeCreation(flowNodesRef, {
    when: 'update',
    ignore: { source: true },
  });

  // `flow` can be undefined briefly after a source is created — `flowNodes`
  // hasn't been populated by `loadPipeline` yet. Without this guard, the
  // downstream `useTriggeringFlows` reduce crashes on `flow.flow_triggers`.
  const flowArray = useMemo(() => (flow ? [flow] : []), [flow]);

  const { triggeringMap, pipelineItems, orderItems, linksMap } = usePipelinesItems(flowArray, {
    ignoreProject: true,
  });

  function handleMouseLeave() {
    props.deselectAllPipelineItems?.();
  }

  const onClickCollapseFlow = () => {
    history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}`);
  };

  // Layout: URL query `?LayoutDirection=TB|LR` (or `layoutDirection`) wins on load
  // and when the query changes; toolbar toggle updates the URL so links are shareable.
  // Flow types whose renderer doesn't support the toggle (rag, api_server) ignore
  // the URL param entirely — otherwise users could bypass the hidden toolbar button.
  const supportsLayoutToggle = flowTypeSupportsLayoutToggle(flow?.flow_type);
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>(() =>
    supportsLayoutToggle ? (layoutDirectionFromSearchParams(search) ?? 'LR') : 'LR',
  );

  useEffect(() => {
    if (!supportsLayoutToggle) {
      setLayoutDirection('LR');
      return;
    }
    const fromUrl = layoutDirectionFromSearchParams(search);
    setLayoutDirection(fromUrl ?? 'LR');
  }, [search, supportsLayoutToggle]);

  const toggleLayoutDirection = useCallback(() => {
    if (!supportsLayoutToggle) return;
    setLayoutDirection((prev) => {
      const next: LayoutDirection = prev === 'LR' ? 'TB' : 'LR';
      const params = new URLSearchParams(
        search && search.startsWith('?') ? search.slice(1) : search,
      );
      params.set('LayoutDirection', next);
      const nextSearch = params.toString();
      history.replace({
        pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      });
      return next;
    });
  }, [history, pathname, search, supportsLayoutToggle]);

  const toolbarItems = useMemo(
    () =>
      buildToolbarItems({
        isPreview,
        isFullScreenCanvas,
        isEmbeddedMode,
        onClickExpandFlow,
        onClickCollapseFlow,
        refreshPipeline,
        flowType: flow?.flow_type,
        layoutDirection,
        toggleLayoutDirection,
      }),
    [
      isPreview,
      isFullScreenCanvas,
      isEmbeddedMode,
      onClickExpandFlow,
      refreshPipeline,
      flow?.flow_type,
      layoutDirection,
      toggleLayoutDirection,
    ],
  );

  // Single read point for the whole canvas-building pipeline (mappers.ts,
  // createEdges.ts, dagreLayout.ts, utils.ts, ResourceNode.tsx, LastRunNode.tsx
  // all read this off nodeProps rather than each calling the hook themselves —
  // most of them are plain functions several calls deep, not components).
  const byoV2Enabled = useFeatureFlag('UI_ENABLE_BYO_V2');

  // `?highlight=<flow_node_id>` (e.g. a resource link in Flow Insights) marks one
  // node. Separate from selectedItem, which drives the existing branch highlight.
  const highlightedNodeKey = useMemo(() => new URLSearchParams(search).get('highlight'), [search]);

  // `?select=<flow_node_id>` (the resource links in Flow Insights → Run History) opens
  // that node's detail panel once. The param is dropped after it opens so closing the
  // panel doesn't reopen it. An id that matches no node does nothing.
  const selectedNodeKey = useMemo(() => new URLSearchParams(search).get('select'), [search]);
  const onSelectedNodeOpened = useCallback(() => {
    const params = new URLSearchParams(search);
    params.delete('select');
    const nextSearch = params.toString();
    history.replace({ pathname, search: nextSearch ? `?${nextSearch}` : '' });
  }, [history, pathname, search]);

  const nodeProps = useMemo(
    () => ({
      flow,
      byoV2Enabled,
      hideToolbar: (item) => {
        return flow ? item.id === flow.id : false;
      },
      selectedFlow: flow
        ? (Object.values(pipelineItems).find((i: any) => i.id === flow.id) as any)?.flow_node_id
        : undefined,
      selectedItem: props.selectedItem,
      select: props.select,
      branches: props.branches,
      longestDataSetChainLength: props.longestDataSetChainLength,
      connectionTypes: props.connectionTypes,
      isPreview: props.isPreview,
      isFlowRunPreview: props.isFlowRunPreview,
      getItemCard: props.getItemCard,
      onItemClick: props.onItemClick,
      // Mark as project flow if projectId is provided (we're viewing a project)
      isProjectFlow: !!props.projectId,
      // BYO create mode: callback forwarded to ByoSourceShellNode via nodeProps spread.
      onByoSourceClick: props.byoCreate?.onSourceClick,
      // BYO create mode: CodeNode/ByoDestShellNode use this to skip the
      // "assume configured, verify, flicker" backfill dance entirely — a
      // source fresh out of Step 1 cannot have code yet.
      isCreateFlow: !!props.byoCreate,
      // Suppress the metrics interval toggle on quick/structural previews
      // (e.g. the flow-preview modal) where a time control would be misleading.
      hideMetricsSelector: props.hideMetricsSelector,
      highlightedNodeKey,
      selectedNodeKey,
      onSelectedNodeOpened,
    }),
    [
      flow,
      byoV2Enabled,
      props.selectedItem,
      props.select,
      props.branches,
      props.longestDataSetChainLength,
      props.connectionTypes,
      props.isPreview,
      props.isFlowRunPreview,
      props.getItemCard,
      props.onItemClick,
      props.projectId,
      props.byoCreate,
      props.hideMetricsSelector,
      highlightedNodeKey,
      selectedNodeKey,
      onSelectedNodeOpened,
      layoutDirection,
    ],
  );

  // BYO create mode: virtual SOURCE shell item shown before any real source exists.
  const byoShellItems = useMemo(() => {
    if (!props.byoCreate || flow) return null;
    const id = 'byo-source-shell';
    return {
      [id]: {
        type: 'SOURCE',
        flow_node_id: id,
        id,
        node_type: 'data_source',
        flow_type: 'custom',
        isByoSourceShell: true,
        origin_node_id: id,
        excludeFlowControlNode: true,
        children: [],
        linked_flows: [],
        flow_triggers: [],
      },
    };
  }, [props.byoCreate, flow]);

  const needsContainer = flow?.linked_flows?.length > 0 || flow?.flow_triggers?.length > 0;

  const isSqlAutomation = useMemo(() => {
    const itemsForFlow = needsContainer ? pipelineItems : items;
    const rootSource = Object.values(itemsForFlow || {}).find(
      (i: any) => i.type === 'SOURCE' && !i.parent_node_id,
    );
    if (!rootSource) return false;
    return isSqlAutomationSource(rootSource, entities?.vendors ?? {});
  }, [needsContainer, pipelineItems, items, entities]);

  // Embedded iframe has no app header; use 100% height chain instead of 100vh offsets.
  const fullScreenHeight = isEmbeddedMode ? '100%' : 'calc(100vh - 56px)';

  return (
    <ResourceEditProvider>
      <div
        className="view-source-pipeline"
        style={
          fullScreen
            ? {
                ...styles.mainContent,
                height: fullScreenHeight,
                marginBottom: 0,
              }
            : isFlowRunPreview
              ? { ...styles.mainContent, marginBottom: 0 }
              : styles.mainContent
        }
        ref={onRootRef}
      >
        <div className="pipeline-container" onMouseLeave={handleMouseLeave}>
          <div
            className="inner-container"
            style={{
              height: fullScreen ? fullScreenHeight : CANVAS_HEIGHT,
            }}
          >
            {flow?.flow_type === 'rag' ? (
              <RAGFlowNodes
                toolbarItems={toolbarItems}
                rootNodeId={flow.origin_node_id}
                layoutDirection={layoutDirection}
              >
                <RAGAPIUsage />
              </RAGFlowNodes>
            ) : flow?.flow_type === 'api_server' ? (
              <ApiServerFlowNodes
                toolbarItems={toolbarItems}
                rootNodeId={flow.origin_node_id}
                nodeProps={nodeProps}
                layoutDirection={layoutDirection}
              >
                <ApiRequestModal />
              </ApiServerFlowNodes>
            ) : (
              <FlowNodes
                ref={flowNodesRef}
                id={props.flow?.id}
                triggeringMap={triggeringMap}
                hideMiniMap
                flowsOrder={needsContainer ? orderItems : undefined}
                keepFlowsOrder
                disableHashing={disableHashing}
                miniMapPosition="bottom-left"
                miniMapStyle={minimapStyle}
                toolbarItems={toolbarItems}
                pipelineItems={byoShellItems ?? (needsContainer ? pipelineItems : items)}
                linksMap={linksMap}
                nodeProps={nodeProps}
                isSqlAutomation={isSqlAutomation}
                layoutDirection={layoutDirection}
              />
            )}
          </div>
        </div>
      </div>
    </ResourceEditProvider>
  );
};

SourcePipeline.displayName = 'SourcePipeline';

// NOTE: this custom comparator intentionally re-renders ONLY when the selected
// flow / pipelineItems / flow object change — it ignores all callback props
// (refreshPipeline, onItemClick, select, getItemCard, onClickExpandFlow, …) to
// avoid re-rendering the heavy canvas on every parent render. CONTRACT: any
// handler passed to SourcePipeline must therefore have a STABLE identity and read
// live data at call-time (e.g. via a ref or this.props), because a changed handler
// reference will NOT propagate to children (toolbar buttons, node actions). A
// changing-identity callback that closes over state will silently go stale here.
// See NEX-21823 (refreshPipeline ignoring the metrics toggle).
export default memo(
  SourcePipeline,
  (prevProps: SourcePipelineProps, nextProps: SourcePipelineProps) => {
    const prevDataSourceId =
      prevProps.dataSource?.origin_node_id || prevProps.dataSource?.flow_node_id;
    const nextDataSourceId =
      nextProps.dataSource?.origin_node_id || nextProps.dataSource?.flow_node_id;

    if (prevProps.isFlowRunPreview !== nextProps.isFlowRunPreview) return false;

    // Always re-render if dataSource changes (different flow selected)
    if (prevDataSourceId !== nextDataSourceId) {
      return false;
    }

    return (
      isEqual(prevProps.pipelineItems, nextProps.pipelineItems) &&
      isEqual(prevProps.flow, nextProps.flow)
    );
  },
);
